import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useAppGlass } from "./glass";
import { getAuthHeaders } from "./auth";

const emptyForm = {
  name: "",
  description: "",
  pricingModel: "flat",
  pricePerSqFt: "",
  priceDisplay: "",
  imageUrl: "",
  sortOrder: 0,
  active: true,
};

/**
 * Admin: catalog of services shown on the quote page (Local services + quote payload).
 */
function CreateJob() {
  const g = useAppGlass();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState("");
  const [quotePageUrlHint, setQuotePageUrlHint] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get("/catalog-services/all", { headers: getAuthHeaders() });
      const list = data.services || [];
      setRows(list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
    } catch {
      setError("Could not load services. Sign in as an administrator and try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get("/user/settings", { headers: getAuthHeaders() })
      .then(({ data }) => {
        if (cancelled) return;
        const s = typeof data.quoteSlug === "string" ? data.quoteSlug.trim().toLowerCase() : "";
        setQuotePageUrlHint(s ? `${window.location.origin}/quote/${s}` : "");
      })
      .catch(() => {
        if (!cancelled) setQuotePageUrlHint("");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm, sortOrder: rows.length });
    setDialogError("");
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({
      name: s.name,
      description: s.description || "",
      pricingModel: s.pricingModel === "per_sqft" ? "per_sqft" : "flat",
      pricePerSqFt:
        s.pricingModel === "per_sqft" && s.pricePerSqFt != null && Number.isFinite(s.pricePerSqFt)
          ? String(s.pricePerSqFt)
          : "",
      priceDisplay: s.priceDisplay,
      imageUrl: s.imageUrl || "",
      sortOrder: s.sortOrder ?? 0,
      active: s.active !== false,
    });
    setDialogError("");
    setDialogOpen(true);
  };

  const save = async () => {
    setDialogError("");
    if (form.pricingModel === "per_sqft") {
      const r = parseFloat(String(form.pricePerSqFt).trim(), 10);
      if (!Number.isFinite(r) || r <= 0) {
        setDialogError("Enter a positive price per square foot (USD).");
        return;
      }
    } else if (!form.priceDisplay.trim()) {
      setDialogError("Enter a price label for flat pricing (e.g. $45).");
      return;
    }
    setSaving(true);
    try {
      let priceDisplayOut = form.priceDisplay.trim();
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        pricingModel: form.pricingModel,
        imageUrl: form.imageUrl.trim(),
        sortOrder: form.sortOrder,
        active: form.active,
      };
      if (form.pricingModel === "per_sqft") {
        const r = parseFloat(String(form.pricePerSqFt).trim(), 10);
        payload.pricePerSqFt = r;
        if (!priceDisplayOut) {
          priceDisplayOut = `$${r.toFixed(4).replace(/\.?0+$/, "")}/sq ft`;
        }
      }
      payload.priceDisplay = priceDisplayOut;
      if (editingId) {
        const { data } = await axios.patch(`/catalog-services/${editingId}`, payload, {
          headers: getAuthHeaders(),
        });
        setRows((prev) =>
          prev
            .map((r) => (r.id === editingId ? data.service : r))
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        );
      } else {
        const { data } = await axios.post("/catalog-services", payload, { headers: getAuthHeaders() });
        setRows((prev) =>
          [...prev, data.service].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        );
      }
      setDialogOpen(false);
    } catch (err) {
      setDialogError(err.response?.data?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this service from the quote page and quote form?")) return;
    try {
      await axios.delete(`/catalog-services/${id}`, { headers: getAuthHeaders() });
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      setError(err.response?.data?.message || "Delete failed.");
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          ...g.panel,
          borderRadius: 3,
          p: 2.5,
          mb: 2,
        }}
      >
        <Typography variant="h6" fontWeight={700} letterSpacing="-0.02em" gutterBottom>
          My services (public catalog)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
          These entries power <strong>Local services</strong> on <strong>your</strong> quote page (URL under{" "}
          <strong>Website</strong>) and what customers can add to a <strong>Fast quote</strong>. Active services appear in
          order of <em>Sort order</em> (lower first).
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2 }}>
          <Button variant="contained" onClick={openAdd}>
            Add service
          </Button>
          <Button variant="outlined" onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Box>
        {error ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Loading catalog…
            </Typography>
          </Box>
        ) : rows.length === 0 ? (
          <Typography component="div" variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No services on your quote page yet. Add at least one above — only your customers on{" "}
            {quotePageUrlHint ? (
              <Box component="strong" sx={{ wordBreak: "break-all" }}>
                {quotePageUrlHint}
              </Box>
            ) : (
              "your quote link (Website tab)"
            )}{" "}
            will see them. (The generic <strong>/quote</strong> page still shows the platform default catalog.)
          </Typography>
        ) : (
          rows.map((s) => (
            <Card
              key={s.id}
              sx={{
                mb: 2,
                borderRadius: 2,
                border: "1px solid rgba(255,255,255,0.65)",
                bgcolor: "rgba(255,255,255,0.45)",
              }}
            >
              <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {s.name}
                      {!s.active ? (
                        <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                          (hidden on site)
                        </Typography>
                      ) : null}
                    </Typography>
                    <Typography variant="body2" color="primary" fontWeight={600}>
                      {s.pricingModel === "per_sqft" && s.pricePerSqFt != null && Number.isFinite(s.pricePerSqFt)
                        ? `$${s.pricePerSqFt}/sq ft · ${s.priceDisplay}`
                        : s.priceDisplay}
                    </Typography>
                    {s.description ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.45 }}>
                        {s.description}
                      </Typography>
                    ) : null}
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Sort {s.sortOrder ?? 0}
                      {s.resolvedImageUrl
                        ? ` · Image: ${s.hasStoredImage && !s.imageUrl ? "stored in database" : s.resolvedImageUrl}`
                        : ""}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                    <IconButton aria-label="Edit service" onClick={() => openEdit(s)} size="small" color="primary">
                      <EditOutlinedIcon />
                    </IconButton>
                    <IconButton aria-label="Delete service" onClick={() => remove(s.id)} size="small" color="error">
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? "Edit service" : "Add service"}</DialogTitle>
        <DialogContent>
          {dialogError ? (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {dialogError}
            </Alert>
          ) : null}
          <TextField
            label="Name"
            required
            fullWidth
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            sx={{ mt: 1, mb: 2, ...g.textField }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="pricing-model-label">Pricing strategy</InputLabel>
            <Select
              labelId="pricing-model-label"
              label="Pricing strategy"
              value={form.pricingModel}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  pricingModel: e.target.value,
                  pricePerSqFt: e.target.value === "flat" ? "" : f.pricePerSqFt,
                }))
              }
            >
              <MenuItem value="flat">Flat rate — one price shown on the card</MenuItem>
              <MenuItem value="per_sqft">Per square foot — total from yard estimate</MenuItem>
            </Select>
          </FormControl>
          {form.pricingModel === "per_sqft" ? (
            <TextField
              label="Price per sq ft (USD)"
              required
              fullWidth
              type="number"
              inputProps={{ min: 0, step: "0.0001" }}
              value={form.pricePerSqFt}
              onChange={(e) => setForm((f) => ({ ...f, pricePerSqFt: e.target.value }))}
              sx={{ mb: 2, ...g.textField }}
              helperText="Multiplied by total estimated sq ft from all lots in the quote (map outline or geocoder estimate)."
            />
          ) : null}
          <TextField
            label={form.pricingModel === "per_sqft" ? "Price label (fallback)" : "Price (display)"}
            required={form.pricingModel === "flat"}
            fullWidth
            placeholder={form.pricingModel === "per_sqft" ? "$0.042/sq ft" : "$45"}
            value={form.priceDisplay}
            onChange={(e) => setForm((f) => ({ ...f, priceDisplay: e.target.value }))}
            sx={{ mb: 2, ...g.textField }}
            helperText={
              form.pricingModel === "per_sqft"
                ? "Shown on cards until the homeowner has a yard size. Leave blank to auto-fill from the rate."
                : "Shown on cards and in quote emails exactly as typed."
            }
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={3}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            sx={{ mb: 2, ...g.textField }}
          />
          <TextField
            label="Image URL (optional)"
            fullWidth
            value={form.imageUrl}
            onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))}
            sx={{ mb: 2, ...g.textField }}
            helperText="Optional external URL. Leave blank to use the image stored in the database (default test photos from backfill/seed), or a gradient if none."
          />
          <TextField
            label="Sort order"
            type="number"
            fullWidth
            value={form.sortOrder}
            onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) || 0 }))}
            sx={{ mb: 2, ...g.textField }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              />
            }
            label="Active (show on quote page)"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={
              saving ||
              !form.name.trim() ||
              (form.pricingModel === "flat" && !form.priceDisplay.trim()) ||
              (form.pricingModel === "per_sqft" && !String(form.pricePerSqFt).trim())
            }
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CreateJob;
